import pWaitFor, { TimeoutError } from 'p-wait-for';
import React from 'react';
import type { FC } from 'react';
import { Trans } from '@wix/cli-i18n';
import { Alert, Badge, useAsync } from '@wix/cli-ui-kit';
import { isType, match, variant } from 'variant';
import { getTestOverrides } from '@wix/cli-test-overrides';
import { CliError, CliErrorCode } from '@wix/cli-error';
import type { Site } from '@wix/site-list-client';
import { useDevCenterClient } from '@wix/dev-center-client';
import { OpenBrowser } from './OpenBrowser.js';

interface Props {
  site: Site;
  appId: string;
  onSubmit: () => void;
  onError: (error: unknown) => void;
}

const AppInstallData = variant({
  Success: {},
  Canceled: {},
});

function isAbortError(error: unknown) {
  return (
    error &&
    typeof error === 'object' &&
    'name' in error &&
    error.name === 'AbortError'
  );
}

export const WaitForAppToBeInstalled: FC<Props> = ({
  appId,
  site,
  onSubmit,
  onError,
}) => {
  const { isAppInstalled } = useDevCenterClient({ siteId: site.id });

  const { status } = useAsync(
    async ({ signal }) => {
      const { pollInterval } = getTestOverrides();

      try {
        await pWaitFor(() => isAppInstalled(appId), {
          // Polling interval is set to 6.5 seconds to avoid rate limiting (429)
          interval: pollInterval ?? 6500,
          timeout: {
            milliseconds: 60 * 1000 * 2,
            signal,
          },
        });

        return AppInstallData.Success();
      } catch (error) {
        if (error instanceof TimeoutError) {
          throw new CliError({
            code: CliErrorCode.SiteSelectorTimedout(),
            cause: error,
          });
        }

        if (isAbortError(error)) {
          return AppInstallData.Canceled();
        }

        throw error;
      }
    },
    [],
    {
      onError,
      onSuccess: (result) => {
        if (isType(result, AppInstallData.Success)) {
          onSubmit();
        }
      },
    }
  );

  return match(status, {
    Error: () => null,
    Loading: () => <OpenBrowser siteId={site.id} appId={appId} />,
    Success: () => (
      <Alert type="success">
        <Badge skin="success">
          <Trans i18nKey="general.success" />
        </Badge>{' '}
        <Trans i18nKey="install_app_on_site.finished_successfully" />
      </Alert>
    ),
  });
};
